import { useState } from "react";
import { Outlet } from "react-router-dom";
import { Sidebar } from "@/components/Sidebar";
import { SearchDialog } from "@/components/SearchDialog";
import { useUI } from "@/context/UIContext";
import { useApp } from "@/context/AppContext";
import { Search, Plus, Palette, Menu, X, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { getThemeById } from "@/lib/presets";
import { AnimatePresence, motion } from "framer-motion";

function ThemeQuickSwitch() {
  const { presetThemes, customThemes, settings, updateSettings, applyTheme } = useApp();
  const all = [...presetThemes, ...customThemes];
  const activeId = settings?.active_theme_id;
  const pick = (t) => { applyTheme(t.variables, t.type); updateSettings({ active_theme_id: t.id }); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-105" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }} data-testid="theme-quick-switch">
          <Palette size={18} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3 rounded-2xl border-0" style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }}>
        <p className="text-xs font-semibold uppercase tracking-wider mb-2 px-1" style={{ color: "var(--text-muted)" }}>Quick Theme</p>
        <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto hide-scrollbar">
          {all.map((t) => {
            const active = t.id === activeId;
            return (
              <button key={t.id} onClick={() => pick(t)} className="relative p-2 rounded-xl text-left transition-transform hover:scale-[1.03]"
                style={{ background: t.variables["--bg-card"], border: `1px solid ${active ? "var(--accent-primary)" : "var(--border-subtle)"}` }}>
                <div className="flex gap-1 mb-1.5">
                  {["--accent-primary", "--timeline-indicator", "--text-primary"].map((k) => <span key={k} className="w-4 h-4 rounded-full" style={{ background: t.variables[k], border: "1px solid rgba(255,255,255,0.1)" }} />)}
                </div>
                <span className="text-[11px] font-medium truncate block" style={{ color: t.variables["--text-primary"] }}>{t.name}</span>
                {active && <Check size={13} className="absolute top-2 right-2" style={{ color: "var(--accent-primary)" }} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function Layout() {
  const { setSearchOpen, openEventModal } = useUI();
  const [mobileNav, setMobileNav] = useState(false);

  return (
    <div className="flex h-screen w-full overflow-hidden" style={{ background: "var(--bg-app)" }}>
      {/* Desktop sidebar */}
      <div className="hidden lg:flex"><Sidebar /></div>

      {/* Mobile sidebar */}
      <AnimatePresence>
        {mobileNav && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileNav(false)} />
            <motion.div initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "spring", stiffness: 320, damping: 32 }} className="fixed inset-y-0 left-0 z-50 lg:hidden">
              <Sidebar onNavigate={() => setMobileNav(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center gap-3 px-4 lg:px-6 h-16 shrink-0" style={{ borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-app)" }}>
          <button className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }} onClick={() => setMobileNav(true)} data-testid="mobile-nav-toggle">
            <Menu size={20} />
          </button>

          <button onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2.5 h-10 px-3.5 rounded-xl flex-1 max-w-md transition-colors hover:opacity-90"
            style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", color: "var(--text-muted)" }} data-testid="topbar-search">
            <Search size={16} />
            <span className="text-sm">Search everything...</span>
            <kbd className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded hidden sm:block" style={{ background: "var(--bg-timeline-track)" }}>⌘K</kbd>
          </button>

          <div className="ml-auto flex items-center gap-2.5">
            <ThemeQuickSwitch />
            <button onClick={() => openEventModal({})}
              className="h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold text-white transition-all hover:scale-105 shadow-lg"
              style={{ background: "var(--accent-primary)", boxShadow: "0 4px 16px var(--accent-glow)" }} data-testid="button-new-event">
              <Plus size={18} /> <span className="hidden sm:inline">New Event</span>
            </button>
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </main>
      </div>

      <SearchDialog />
    </div>
  );
}
